import React, { useMemo } from 'react';
import { Button, Checkbox, Divider, Flex, message } from 'antd';
import { LeftTailArrowIcon } from '@/assets/icons/icons';
import SearchInput from '@/components/SearchInput';
import type { Team } from './AudienceSelector';
import { truncateByLength } from '@/common/helper';
import { AllCommonTeams } from '../ReplyCommentBox';
import { CustomShipmentText } from '@/components';

interface TeamMenuProps {
  type: 'my_team' | 'specific_teams';
  teams: Team[] | AllCommonTeams[];
  selectedTeamIds: string[];
  search: string;
  validationError: string | null;
  onBack: () => void;
  onSearchChange: (val: string) => void;
  onSearchClear: () => void;
  onSelectionChange: (val: string[]) => void;
  onApply: (selectedIds: string[]) => void;
  onReset: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

const TeamMenu: React.FC<TeamMenuProps> = ({
  type,
  teams,
  selectedTeamIds,
  search,
  validationError,
  onBack,
  onSearchChange,
  onSearchClear,
  onSelectionChange,
  onApply,
  onReset,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const filteredTeams = useMemo(
    () =>
      teams.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())),
    [teams, search]
  );

  const title = type === 'my_team' ? 'My Teams' : 'Teams';
  const isMyTeam = type === 'my_team';

  const handleApply = () => {
    if (selectedTeamIds.length === 0) {
      message.error('Select at least one team');
      return;
    }
    onApply(selectedTeamIds);
    onSearchClear();
    message.success(
      `Visibility set: ${isMyTeam ? 'My teams' : 'Specific teams'} (${selectedTeamIds.length})`
    );
  };

  return (
    <div className='COC-audience-dropdown is-Team'>
      {/* Header */}
      <Flex
        gap={8}
        align='center'
        className='cursor-pointer'
        style={{ padding: '8px 0' }}
        onClick={onBack}
      >
        <LeftTailArrowIcon />
        <div className='zl-heading-12 gray-5e'>{title}</div>
      </Flex>

      {/* Search bar */}
      {teams?.length > 10 && (
        <div className='mb-2'>
          <SearchInput
            placeholder='Search teams'
            width='100%'
            onChange={onSearchChange}
            onClear={onSearchClear}
          />
        </div>
      )}

      {/* Checkbox list */}
      <Flex gap={8} vertical style={{ maxHeight: 220, overflowY: 'auto' }}>
        <Checkbox.Group
          value={selectedTeamIds.filter((id) =>
            filteredTeams.some((t) => t.id === id)
          )}
          onChange={(vals: string[]) => {
            const newSelected = [
              ...selectedTeamIds.filter(
                (id) => !filteredTeams.some((t) => t.id === id)
              ),
              ...vals,
            ];
            onSelectionChange(newSelected);
          }}
        >
          <Flex vertical gap={6} style={{ width: '100%' }}>
            {filteredTeams?.map((t) => (
              <Flex
                align='center'
                gap={12}
                className='home-filter-checkbox py-1-5 px-1'
                key={t?.id}
              >
                <Checkbox
                  className='custom-checkbox-12 zl-para-400-12'
                  value={t?.id}
                />
                <span
                  className='team-color-circle'
                  style={{ background: t?.color ?? '#000' }}
                />
                <CustomShipmentText text={truncateByLength(t?.name, 20)} />
              </Flex>
            ))}

            {filteredTeams?.length === 0 && (
              <div className='disabled-row p-4'>No teams match your search</div>
            )}

            {type === 'specific_teams' && hasMore && (
              <Flex justify='center' style={{ padding: '8px 0' }}>
                <Button
                  type='link'
                  size='small'
                  loading={loadingMore}
                  onClick={onLoadMore}
                  style={{ fontSize: '11px', padding: 0 }}
                >
                  Load More
                </Button>
              </Flex>
            )}
          </Flex>
        </Checkbox.Group>
      </Flex>

      {/* Validation message */}
      {validationError && (
        <div style={{ color: '#d4380d', padding: '8px 12px', fontSize: 12 }}>
          {validationError}
        </div>
      )}

      {/* Footer buttons */}
      {filteredTeams?.length > 0 && (
        <>
          <Divider style={{ margin: 0 }} />
          <Flex justify='end' gap={8} className='mt-2'>
            <button
              onClick={onReset}
              className='zl-btn zl-transparent-btn radius-8'
            >
              Reset
            </button>
            <button onClick={handleApply} className='zl-btn secondary radius-8'>
              Apply
            </button>
          </Flex>
        </>
      )}
    </div>
  );
};

export default TeamMenu;
